/**
 * MediaLightbox — Lightbox reutilizável para visualização de mídias.
 * Suporta imagem (zoom, rotação, pan), vídeo (autoplay) e áudio (player).
 * Opcionalmente aceita navegação em carrossel (onPrev/onNext + setas de teclado).
 *
 * Usado no chat (ImageContent, VideoContent) e nos arquivos do contato (FilesPanel).
 */
import { useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  X,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Download,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  AudioLines,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { downloadMedia } from "@/lib/mediaDownload";

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {string} props.src - URL da mídia
 * @param {string} [props.title] - Título (filename, caption, etc)
 * @param {string} [props.subtitle] - Info secundária (data, ticket, contador)
 * @param {"image"|"video"|"audio"} [props.type="image"]
 * @param {() => void} [props.onPrev] - Callback para item anterior (mostra seta esquerda)
 * @param {() => void} [props.onNext] - Callback para próximo item (mostra seta direita)
 * @param {React.ReactNode} [props.children] - DialogTrigger (opcional)
 */
export function MediaLightbox({
  open,
  onOpenChange,
  src,
  title,
  subtitle,
  type = "image",
  onPrev,
  onNext,
  children,
}) {
  // ─── Image-only controls ───
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const panRef = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);

  // Reset view when src changes (carousel navigation)
  useEffect(() => {
    setRotation(0);
    setZoom(1);
    panRef.current = { x: 0, y: 0 };
  }, [src]);

  const reset = useCallback(() => {
    setRotation(0);
    setZoom(1);
    panRef.current = { x: 0, y: 0 };
  }, []);

  const handleOpenChange = useCallback(
    (isOpen) => {
      if (!isOpen) reset();
      onOpenChange(isOpen);
    },
    [onOpenChange, reset],
  );

  // Keyboard: left/right arrows for carousel
  useEffect(() => {
    if (!open || (!onPrev && !onNext)) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" && onPrev) {
        e.preventDefault();
        onPrev();
      }
      if (e.key === "ArrowRight" && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onPrev, onNext]);

  const buildTransform = (pan, rot, z) =>
    `translate(${pan.x}px, ${pan.y}px) rotate(${rot}deg) scale(${z})`;

  const isImage = type === "image";
  const isVideo = type === "video";
  const isAudio = type === "audio";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children}
      <DialogContent
        className={cn(
          "w-full p-0 border-border [&>button]:hidden",
          isAudio ? "max-w-lg bg-surface" : "max-w-4xl bg-black/95",
        )}
        aria-describedby={undefined}
      >
        <div className="relative">
          {/* Header */}
          <DialogHeader
            className={cn(
              "z-20 p-4",
              isAudio
                ? "border-b border-border"
                : "absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent",
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0 pr-10">
                <DialogTitle
                  className={cn(
                    "text-sm font-medium truncate",
                    isAudio ? "text-foreground" : "text-white",
                  )}
                >
                  {title || (isAudio ? "Áudio" : isVideo ? "Vídeo" : "Imagem")}
                </DialogTitle>
                {subtitle && (
                  <p
                    className={cn(
                      "text-xs mt-0.5",
                      isAudio ? "text-foreground-muted" : "text-neutral-400",
                    )}
                  >
                    {subtitle}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleOpenChange(false)}
                className={cn(
                  "h-8 w-8 rounded-full flex-shrink-0",
                  isAudio
                    ? "text-foreground-muted hover:text-foreground hover:bg-surface-hover"
                    : "text-white/70 hover:text-white hover:bg-white/10",
                )}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          {/* Carousel arrows */}
          {onPrev && (
            <button
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white/80 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm"
              aria-label="Anterior"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {onNext && (
            <button
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white/80 hover:text-white flex items-center justify-center transition-colors backdrop-blur-sm"
              aria-label="Próximo"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}

          {/* ─── Content ─── */}
          {isAudio ? (
            <div className="flex flex-col items-center justify-center py-16 px-8 gap-6">
              <div className="h-24 w-24 rounded-full bg-violet-500/20 flex items-center justify-center">
                <AudioLines className="h-12 w-12 text-violet-400" />
              </div>
              <audio src={src} controls autoPlay className="w-full max-w-md" />
            </div>
          ) : isVideo ? (
            <div className="flex items-center justify-center min-h-[400px] max-h-[80vh] overflow-hidden">
              <video
                src={src}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] object-contain"
              />
            </div>
          ) : (
            /* Image with zoom/pan/rotate */
            <div
              className={cn(
                "flex items-center justify-center min-h-[400px] max-h-[80vh] overflow-hidden select-none",
                zoom > 1
                  ? "cursor-grab active:cursor-grabbing"
                  : "cursor-default",
              )}
              onWheel={(e) => {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.25 : 0.25;
                setZoom((prev) => {
                  const next = Math.max(1, Math.min(prev + delta, 5));
                  if (next === 1) panRef.current = { x: 0, y: 0 };
                  return next;
                });
              }}
              onPointerDown={(e) => {
                if (zoom <= 1) return;
                isDragging.current = true;
                dragStart.current = { x: e.clientX, y: e.clientY };
                if (imgRef.current) imgRef.current.style.transition = "none";
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (!isDragging.current) return;
                const dx = e.clientX - dragStart.current.x;
                const dy = e.clientY - dragStart.current.y;
                const pan = {
                  x: panRef.current.x + dx,
                  y: panRef.current.y + dy,
                };
                if (imgRef.current) {
                  imgRef.current.style.transform = buildTransform(
                    pan,
                    rotation,
                    zoom,
                  );
                }
              }}
              onPointerUp={(e) => {
                if (!isDragging.current) return;
                isDragging.current = false;
                const dx = e.clientX - dragStart.current.x;
                const dy = e.clientY - dragStart.current.y;
                panRef.current = {
                  x: panRef.current.x + dx,
                  y: panRef.current.y + dy,
                };
                if (imgRef.current) imgRef.current.style.transition = "";
              }}
              onDoubleClick={() => {
                if (zoom > 1) {
                  setZoom(1);
                  panRef.current = { x: 0, y: 0 };
                } else {
                  setZoom(2);
                }
              }}
            >
              <img
                ref={imgRef}
                src={src}
                alt={title || "Imagem"}
                draggable={false}
                className="max-w-full max-h-[80vh] object-contain transition-transform duration-200"
                style={{
                  transform: buildTransform(panRef.current, rotation, zoom),
                }}
              />
            </div>
          )}

          {/* Action buttons */}
          <div
            className={cn(
              "flex justify-center gap-2 z-20",
              isAudio
                ? "p-4 border-t border-border"
                : "absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent",
            )}
          >
            {isImage && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setZoom((prev) => {
                      const next = Math.max(prev - 0.5, 1);
                      if (next === 1) panRef.current = { x: 0, y: 0 };
                      return next;
                    })
                  }
                  disabled={zoom <= 1}
                  className="bg-surface-active hover:bg-border-strong"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setZoom((prev) => Math.min(prev + 0.5, 5))}
                  disabled={zoom >= 5}
                  className="bg-surface-active hover:bg-border-strong"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setRotation((prev) => (prev + 90) % 360)}
                  className="bg-surface-active hover:bg-border-strong"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.open(src, "_blank")}
              className="bg-surface-active hover:bg-border-strong"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Abrir original
            </Button>
            {/* O atributo `download` de um `<a>` é IGNORADO quando a URL é de
                outra origem — e o S3 é outra origem. Com `target="_blank"`
                junto, o resultado era abrir o arquivo numa aba nova em vez de
                baixar. Quem manda baixar tem que ser o servidor. */}
            <Button
              variant="secondary"
              size="sm"
              className="bg-surface-active hover:bg-border-strong"
              onClick={() => downloadMedia(src, title)}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
